import { useRestApiProvider } from "@jugl-web/rest-api";
import { TaskLabel } from "@jugl-web/rest-api/tasks";
import { assert, reorder, useTranslations } from "@jugl-web/utils";
import { useCallback, useMemo } from "react";
import { useTaskInitQuery } from "./useTaskInitQuery";

type NewTaskLabel = Pick<TaskLabel, "text" | "color">;

const EMPTY_ARRAY: [] = [];

export interface UseTaskLabelsOptions {
  entityId: string | undefined;
}

export const useTaskLabels = ({ entityId }: UseTaskLabelsOptions) => {
  const { data } = useTaskInitQuery({ entityId });

  const { tasksApi } = useRestApiProvider();
  const { t } = useTranslations();

  const labels = data?.labels || EMPTY_ARRAY;

  const labelsMap = useMemo<Map<string, TaskLabel>>(
    () => new Map(labels.map((label) => [label.id, label])),
    [labels]
  );

  const noneLabel = useMemo<TaskLabel>(
    () => ({
      id: "none",
      text: t({ id: "tasks-page.none-label", defaultMessage: "None" }),
      color: "#D2D2D8",
      order: 0,
    }),
    [t]
  );

  const areLabelsSupported = labels.length > 0;

  const getLabelById = useCallback(
    (labelId: string) => labelsMap.get(labelId),
    [labelsMap]
  );

  const [createTaskLabelMutation, { isLoading: isCreatingLabel }] =
    tasksApi.useCreateTaskLabelMutation();

  const [updateTaskLabelMutation, { isLoading: isUpdatingLabel }] =
    tasksApi.useUpdateTaskLabelMutation();

  const [updateTaskLabelsInBulkMutation] =
    tasksApi.useUpdateTaskLabelsInBulkMutation();

  const [deleteTaskLabelMutation, { isLoading: isDeletingLabel }] =
    tasksApi.useDeleteTaskLabelMutation();

  // #region Mutations
  const createLabel = useCallback(
    async (newLabel: NewTaskLabel) => {
      assert(!!entityId);

      return new Promise<void>((resolve, reject) => {
        createTaskLabelMutation({
          entityId,
          label: {
            ...newLabel,
            order: labels.length > 0 ? labels[labels.length - 1].order + 1 : 1,
          },
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject();
          }
        });
      });
    },
    [createTaskLabelMutation, entityId, labels]
  );

  const updateLabel = useCallback(
    (updatedLabel: TaskLabel) => {
      assert(!!entityId);
      return updateTaskLabelMutation({ entityId, label: updatedLabel });
    },
    [entityId, updateTaskLabelMutation]
  );

  const deleteLabel = useCallback(
    (labelId: string) => {
      assert(!!entityId);
      return deleteTaskLabelMutation({ entityId, labelId });
    },
    [entityId, deleteTaskLabelMutation]
  );

  const reorderLabel = useCallback(
    (startIndex: number, endIndex: number) => {
      assert(!!entityId);

      const updatedLabels = reorder(labels, startIndex, endIndex).map(
        (label, index) => ({ ...label, order: index + 1 })
      );

      updateTaskLabelsInBulkMutation({ entityId, labels: updatedLabels });
    },
    [entityId, labels, updateTaskLabelsInBulkMutation]
  );
  // #endregion

  return {
    labels,
    noneLabel,
    areLabelsSupported,
    getLabelById,

    // Mutations
    createLabel,
    updateLabel,
    deleteLabel,
    reorderLabel,
    isCreatingLabel,
    isUpdatingLabel,
    isDeletingLabel,
  };
};
